import { ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { BrandScopeRoute } from '@/components/common/BrandScopeRoute';
import { LoginDialog } from '@/components/common/LoginDialog';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { RequirePermission } from '@/components/common/RequirePermission';
import { AppLayout } from '@/components/layout/AppLayout';
import {
  LoginDialogProvider,
  useLoginDialog,
} from '@/contexts/LoginDialogContext';
import { TUTTI_V2_NAVIGATION } from '@/navigation/tuttiV2';
import { AcquisitionAttributionsPage } from '@/pages/AcquisitionAttributions';
import {
  AdminCommerceCostDashboardPage,
  AdminCommerceCreditsPage,
  AdminCommerceOrdersPage,
} from '@/pages/AdminCommerce';
import { AdminTokensPage } from '@/pages/AdminTokens';
import { AlertAttributionPage } from '@/pages/AlertAttribution';
import { AppConfigGatewayPrincipalsPage } from '@/pages/AppConfigGatewayPrincipals';
import { ApplicationManagementPage } from '@/pages/ApplicationManagement';
import { BrandManagementPage } from '@/pages/BrandManagement';
import { BrandSelectionPage } from '@/pages/BrandSelection';
import { ComputerProductionUpgradePolicyPage } from '@/pages/ComputerProductionUpgradePolicy';
import { ComputerReleaseNotesPage } from '@/pages/ComputerReleaseNotes';
import { ConnectorManagementPage } from '@/pages/ConnectorManagement';
import { DesktopUpdatesPage } from '@/pages/DesktopUpdates';
import { HTTPExceptionQueryPage } from '@/pages/HTTPExceptionQuery';
import { BetaManagementPage } from '@/pages/InviteCode';
import { LandingWaitlistPage } from '@/pages/LandingWaitlist';
import { LLMAccessPage } from '@/pages/LLMAccess';
import { LLMPricingPage } from '@/pages/LLMPricing';
import { LLMRoutesPage } from '@/pages/LLMRoutes';
import { LoginCallbackPage } from '@/pages/LoginCallback';
import { LoginCodePage } from '@/pages/LoginCode';
import { ProductUpdatesPage } from '@/pages/ProductUpdates';
import { PublicRoomsPage } from '@/pages/PublicRooms';
import { RoomCapacityPage } from '@/pages/RoomCapacity';
import { RoomDebugLogsPage } from '@/pages/RoomDebugLogs';
import { RoomInspectorPage } from '@/pages/RoomInspector';
import { SessionLogsPage } from '@/pages/SessionLogs';
import { SkillManagementPage } from '@/pages/SkillManagement';
import {
  TaskExecutionDetailPage,
  TaskPlatformPage,
  WorkerPoolDetailPage,
} from '@/pages/TaskPlatform';
import { TuttiComputersPage } from '@/pages/TuttiComputers';
import { TuttiPermissionsPage } from '@/pages/TuttiPermissions';
import { UserConfigPage } from '@/pages/UserConfig';
import { VMAgentPackageReviewsPage } from '@/pages/VMAgentPackageReviews';
import { WhitelistPage } from '@/pages/Whitelist';
import { authStore, brandStore } from '@/stores';
import { antdTheme } from '@/theme/antdTheme';
import { applyLocalDevBypass } from '@/utils/localDevBypass';

const TestManagementPage = lazy(() =>
  import('@/pages/TestManagement').then((module) => ({
    default: module.TestManagementPage,
  })),
);

const LegacyBrandRedirect = observer(
  ({ targetPath }: { targetPath: string }) => {
    const activeBrandId = brandStore.activeBrandId;
    if (!activeBrandId) {
      return <Navigate to="/brand-selection" replace />;
    }
    return <Navigate to={`/brands/${activeBrandId}/${targetPath}`} replace />;
  },
);

function AppContent() {
  const { open, closeDialog } = useLoginDialog();

  // 应用启动时初始化认证状态
  useEffect(() => {
    if (applyLocalDevBypass()) {
      return;
    }
    authStore.checkAuth();
  }, []);

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login/callback" element={<LoginCallbackPage />} />
          <Route
            path="/brands/:brandId/test-management"
            element={
              <ProtectedRoute>
                <BrandScopeRoute />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={<p>加载测试管理中…</p>}>
                  <TestManagementPage />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/brand-selection" replace />} />
            <Route
              path="tutti-permissions"
              element={<TuttiPermissionsPage />}
            />
            <Route path="brands" element={<BrandManagementPage />} />
            <Route path="brand-selection" element={<BrandSelectionPage />} />
            <Route
              path="app-config/llm-gateway-principals"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.gatewayPrincipalsView}
                >
                  <AppConfigGatewayPrincipalsPage />
                </RequirePermission>
              }
            />
            <Route
              path="app-config/desktop-updates"
              element={<LegacyBrandRedirect targetPath="desktop-updates" />}
            />
            <Route
              path="vm-agent-package-reviews-v2"
              element={
                <Navigate
                  to={`/${TUTTI_V2_NAVIGATION.routes.agentPackages}`}
                  replace
                />
              }
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.root}
              element={
                <Navigate
                  to={`/${TUTTI_V2_NAVIGATION.routes.agentPackages}`}
                  replace
                />
              }
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.computers}
              element={<TuttiComputersPage />}
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.productUpdates}
              element={<ProductUpdatesPage />}
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.computerReleaseNotes}
              element={<ComputerReleaseNotesPage />}
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.agentPackages}
              element={<VMAgentPackageReviewsPage source="tutti-v2" />}
            />
            <Route
              path="tutti-v2/public-rooms"
              element={<LegacyBrandRedirect targetPath="public-rooms" />}
            />
            <Route
              path={TUTTI_V2_NAVIGATION.routes.computerProductionUpgrade}
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.desktopUpdatesRead}
                >
                  <ComputerProductionUpgradePolicyPage />
                </RequirePermission>
              }
            />
            <Route path="admin-tokens" element={<AdminTokensPage />} />
            <Route
              path="acquisition-attributions"
              element={<AcquisitionAttributionsPage />}
            />
            <Route
              path="room-capacity"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.roomCapacityRead}
                >
                  <RoomCapacityPage />
                </RequirePermission>
              }
            />
            <Route
              path="room-inspector"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.roomInspectorRead}
                >
                  <RoomInspectorPage />
                </RequirePermission>
              }
            />
            <Route
              path="session-logs"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.sessionLogsView}
                >
                  <SessionLogsPage />
                </RequirePermission>
              }
            />
            <Route
              path="tasks"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.taskPlatformRead}
                >
                  <TaskPlatformPage />
                </RequirePermission>
              }
            />
            <Route
              path="tasks/workers/:workerPoolId"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.taskPlatformRead}
                >
                  <WorkerPoolDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="tasks/executions/:taskId"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.taskPlatformRead}
                >
                  <TaskExecutionDetailPage />
                </RequirePermission>
              }
            />
            <Route
              path="skills"
              element={<LegacyBrandRedirect targetPath="skills" />}
            />
            <Route
              path="connectors"
              element={<LegacyBrandRedirect targetPath="connectors" />}
            />
            <Route
              path="alert-attributions/http-failures"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.alertAttributionView}
                >
                  <AlertAttributionPage />
                </RequirePermission>
              }
            />
            <Route
              path="http-exceptions"
              element={
                <RequirePermission
                  permission={ADMIN_PERMISSIONS.alertAttributionView}
                >
                  <HTTPExceptionQueryPage />
                </RequirePermission>
              }
            />
            <Route
              path="app-config/llm-routes"
              element={
                <RequirePermission permission={ADMIN_PERMISSIONS.llmRoutesView}>
                  <LLMRoutesPage />
                </RequirePermission>
              }
            />
            <Route
              path="app-config/llm-pricing"
              element={
                <RequirePermission permission={ADMIN_PERMISSIONS.pricingView}>
                  <LLMPricingPage />
                </RequirePermission>
              }
            />
            <Route
              path="app-config/llm-access-policy"
              element={
                <RequirePermission permission={ADMIN_PERMISSIONS.llmAccessView}>
                  <LLMAccessPage />
                </RequirePermission>
              }
            />

            <Route path="brands/:brandId" element={<BrandScopeRoute />}>
              <Route
                index
                element={<Navigate to="beta-management" replace />}
              />
              <Route path="login-codes" element={<LoginCodePage />} />
              <Route
                path="landing-waitlist"
                element={<LandingWaitlistPage />}
              />
              <Route path="beta-management" element={<BetaManagementPage />} />
              <Route path="whitelist" element={<WhitelistPage />} />
              <Route path="user-configs" element={<UserConfigPage />} />
              <Route path="room-debug-logs" element={<RoomDebugLogsPage />} />
              <Route path="public-rooms" element={<PublicRoomsPage />} />
              <Route
                path="desktop-updates"
                element={
                  <RequirePermission
                    permission={ADMIN_PERMISSIONS.desktopUpdatesRead}
                  >
                    <DesktopUpdatesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="applications"
                element={<ApplicationManagementPage />}
              />
              <Route path="connectors" element={<ConnectorManagementPage />} />
              <Route path="skills" element={<SkillManagementPage />} />
              <Route
                path="commerce/orders"
                element={
                  <RequirePermission
                    permission={ADMIN_PERMISSIONS.commerceOrdersRead}
                  >
                    <AdminCommerceOrdersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="commerce/credits"
                element={
                  <RequirePermission
                    permission={ADMIN_PERMISSIONS.commerceCreditsView}
                  >
                    <AdminCommerceCreditsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="commerce/cost-dashboard"
                element={
                  <RequirePermission
                    permission={ADMIN_PERMISSIONS.commerceCostsView}
                  >
                    <AdminCommerceCostDashboardPage />
                  </RequirePermission>
                }
              />
            </Route>

            <Route
              path="login-codes"
              element={<LegacyBrandRedirect targetPath="login-codes" />}
            />
            <Route
              path="landing-waitlist"
              element={<LegacyBrandRedirect targetPath="landing-waitlist" />}
            />
            <Route
              path="beta-management"
              element={<LegacyBrandRedirect targetPath="beta-management" />}
            />
            <Route
              path="whitelist"
              element={<LegacyBrandRedirect targetPath="whitelist" />}
            />
            <Route
              path="user-configs"
              element={<LegacyBrandRedirect targetPath="user-configs" />}
            />
            <Route
              path="room-debug-logs"
              element={<LegacyBrandRedirect targetPath="room-debug-logs" />}
            />
            <Route
              path="desktop-updates"
              element={<LegacyBrandRedirect targetPath="desktop-updates" />}
            />
            <Route
              path="commerce/orders"
              element={<LegacyBrandRedirect targetPath="commerce/orders" />}
            />
            <Route
              path="commerce/credits"
              element={<LegacyBrandRedirect targetPath="commerce/credits" />}
            />
            <Route
              path="commerce/cost-dashboard"
              element={
                <LegacyBrandRedirect targetPath="commerce/cost-dashboard" />
              }
            />
            <Route
              path="*"
              element={<Navigate to="/brand-selection" replace />}
            />
          </Route>
        </Routes>
      </BrowserRouter>
      <LoginDialog open={open} onOpenChange={closeDialog} />
    </>
  );
}

function App() {
  return (
    <ConfigProvider theme={antdTheme} locale={zhCN}>
      <LoginDialogProvider>
        <AppContent />
      </LoginDialogProvider>
    </ConfigProvider>
  );
}

export default App;
